import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  projectSummarySheetApi,
  CreateProjectSummarySheetDto,
  UpdateProjectSummarySheetDto,
} from "../../../api/projectSummarySheet";

/** Axios + `responseType: "blob"` returns JSON errors as a Blob; surface `error` text in notifications. */
async function axiosBlobErrorMessage(e: unknown): Promise<string> {
  const err = e as { response?: { data?: unknown; status?: number }; message?: string };
  const data = err.response?.data;
  if (data && typeof data === "object" && data !== null && "error" in data && typeof (data as { error: string }).error === "string") {
    const o = data as { error: string; hint?: string; details?: string };
    return [o.error, o.details, o.hint].filter(Boolean).join(" — ");
  }
  if (data instanceof Blob) {
    try {
      const t = await data.text();
      const j = JSON.parse(t) as { error?: string; message?: string; hint?: string; details?: string };
      if (typeof j.error === "string") {
        const parts = [j.error, j.details, j.hint].filter(Boolean);
        return parts.join(" — ");
      }
      if (typeof j.message === "string") return j.message;
    } catch {
      /* ignore */
    }
  }
  return err.message || "Request failed";
}

function saveBlobAsFile(blob: Blob, filename: string, fallbackMime = "application/octet-stream") {
  const mime = blob.type || fallbackMime;
  const fileBlob = new Blob([blob], { type: mime });
  const url = window.URL.createObjectURL(fileBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

export const projectSummarySheetKeys = {
  all: ["projectSummarySheet"] as const,
  lists: () => [...projectSummarySheetKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...projectSummarySheetKeys.lists(), filters] as const,
  details: () => [...projectSummarySheetKeys.all, "detail"] as const,
  detail: (id: string) => [...projectSummarySheetKeys.details(), id] as const,
};

export function useProjectSummarySheets(params?: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: projectSummarySheetKeys.list(params || {}),
    queryFn: async () => {
      const res = await projectSummarySheetApi.list(params);
      return res.data;
    },
  });
}

export function useProjectSummarySheet(id: string) {
  return useQuery({
    queryKey: projectSummarySheetKeys.detail(id),
    queryFn: async () => {
      const res = await projectSummarySheetApi.getById(id);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectSummarySheetDto) => projectSummarySheetApi.create(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      notifications.show({ title: "Created", message: "Project summary sheet saved", color: "green" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed to create",
        color: "red",
      });
    },
  });
}

export function useUpdateProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectSummarySheetDto }) =>
      projectSummarySheetApi.update(id, data).then((r) => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      notifications.show({ title: "Updated", message: "Changes saved", color: "green" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed to update",
        color: "red",
      });
    },
  });
}

export function useDeleteProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectSummarySheetApi.delete(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      notifications.show({ title: "Deleted", message: "Sheet removed", color: "green" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed to delete",
        color: "red",
      });
    },
  });
}

export function useSubmitProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, checkerId }: { id: string; checkerId: string }) =>
      projectSummarySheetApi.submit(id, { checkerId }).then((r) => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      qc.invalidateQueries({ queryKey: ["projectSummarySheet", "pendingApprovals"] });
      notifications.show({ title: "Submitted", message: "Sent for peer check", color: "green" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed to submit",
        color: "red",
      });
    },
  });
}

export function useApprovePeerProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments?: string }) =>
      projectSummarySheetApi.approvePeer(id, comments ? { comments } : undefined).then((r) => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      qc.invalidateQueries({ queryKey: ["projectSummarySheet", "pendingApprovals"] });
      notifications.show({ title: "Approved", message: "Peer check complete", color: "green" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed",
        color: "red",
      });
    },
  });
}

export function useApproveHeadProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments?: string }) =>
      projectSummarySheetApi.approveHead(id, comments ? { comments } : undefined).then((r) => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      qc.invalidateQueries({ queryKey: ["projectSummarySheet", "pendingApprovals"] });
      notifications.show({ title: "Approved", message: "Project summary sheet approved", color: "green" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed",
        color: "red",
      });
    },
  });
}

export function useRejectProjectSummarySheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments: string }) =>
      projectSummarySheetApi.reject(id, { comments }).then((r) => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.detail(id) });
      qc.invalidateQueries({ queryKey: projectSummarySheetKeys.lists() });
      qc.invalidateQueries({ queryKey: ["projectSummarySheet", "pendingApprovals"] });
      notifications.show({ title: "Rejected", message: "Sheet returned to author", color: "orange" });
    },
    onError: (e: any) => {
      notifications.show({
        title: "Error",
        message: e.response?.data?.error || "Failed",
        color: "red",
      });
    },
  });
}

export function useDownloadProjectSummaryDocument() {
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      const res = await projectSummarySheetApi.download(id);
      return { blob: res.data as Blob, fileName };
    },
    onSuccess: ({ blob, fileName }) => {
      saveBlobAsFile(blob, fileName);
      notifications.show({ title: "Download", message: "File saved", color: "green" });
    },
    onError: async (e: unknown) => {
      notifications.show({
        title: "Error",
        message: await axiosBlobErrorMessage(e),
        color: "red",
      });
    },
  });
}

export function useGenerateProjectSummaryPdf() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await projectSummarySheetApi.generatePdf(id);
      return res.data as Blob;
    },
    onSuccess: (blob, id) => {
      saveBlobAsFile(blob, `PR-03-${id}.pdf`, "application/pdf");
      notifications.show({ title: "PDF", message: "Download started", color: "green" });
    },
    onError: async (e: unknown) => {
      notifications.show({
        title: "Error",
        message: await axiosBlobErrorMessage(e),
        color: "red",
      });
    },
  });
}

export function usePendingProjectSummarySheetApprovals() {
  return useQuery({
    queryKey: ["projectSummarySheet", "pendingApprovals"],
    queryFn: async () => {
      const res = await projectSummarySheetApi.getPendingApprovals();
      return res.data;
    },
  });
}
